import React, { createContext, useContext, useState, useEffect } from 'react';
import safeStorage from '../utils/safeStorage';
import config from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status on app load
  useEffect(() => {
    // DEMO MODE: Auto-authenticate for demo access
    console.log('🎭 DEMO MODE: Auto-authenticating for demo access');
    setUser({
      username: 'demo',
      companyName: 'TravelIT Demo',
      email: 'demo@travelit.com',
      features: ['all'],
      expiryDate: null
    });
    setIsAuthenticated(true);
    setIsLoading(false);
    console.log('✅ DEMO MODE: Auto-authenticated');
    
    /* Original auth check - disabled for demo
    console.log('🔍 AuthContext: Checking auth state...');
    const loggedIn = safeStorage.getItem('travelit_logged_in');
    const adminSetup = safeStorage.getItem('travelit_admin_setup');
    console.log('- loggedIn:', loggedIn);
    console.log('- adminSetup exists:', !!adminSetup);
    
    if (loggedIn === 'true' && adminSetup) {
      try {
        const userData = JSON.parse(adminSetup);
        setUser(userData);
        setIsAuthenticated(true);
        console.log('✅ TravelIT: Authentication restored from storage');
      } catch (error) {
        console.error('❌ Error parsing stored user data:', error);
        safeStorage.removeItem('travelit_logged_in');
        safeStorage.removeItem('travelit_admin_setup');
      }
    } else {
      console.log('❌ TravelIT: Pas d\'authentification sauvegardée - state reset');
    }
    setIsLoading(false);
    */
  }, []);

  const login = async (username, password) => {
    setIsLoading(true);
    
    try {
      console.log('🔄 TravelIT: Tentative de connexion...');
      
      // Detect standalone client mode
      const isStandaloneClient = window.electronAPI && !window.location.href.includes('localhost');
      
      if (isStandaloneClient) {
        console.log('🎯 [LOGIN] Standalone client mode - checking local credentials');
        
        // Get locally stored admin user
        const adminUserData = safeStorage.getItem('travelit_admin_user');
        if (!adminUserData) {
          throw new Error('Aucun compte administrateur trouvé. Veuillez refaire la configuration.');
        }
        
        let adminUser;
        try {
          adminUser = JSON.parse(adminUserData);
        } catch (e) {
          throw new Error('Données de compte corrompues. Veuillez refaire la configuration.');
        }
        
        // Validate credentials
        if (adminUser.username !== username || adminUser.password !== password) {
          throw new Error('Identifiants incorrects');
        }
        
        // Successful login
        const userData = {
          id: adminUser.id,
          nom: adminUser.nom,
          prenom: adminUser.prenom,
          email: adminUser.email,
          role: adminUser.role
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        
        // Save authentication state
        safeStorage.setItem('travelit_logged_in', 'true');
        safeStorage.setItem('travelit_admin_setup', JSON.stringify(userData));
        
        console.log('✅ TravelIT: Connexion locale réussie');
        return true;
        
      } else {
        // Development mode - use backend API
        console.log('🔄 [LOGIN] Development mode - using backend API');
        
        const response = await fetch(`${config.apiUrl}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username, password }),
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Erreur de connexion');
        }

        if (data.success && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
          
          // Save authentication state
          safeStorage.setItem('travelit_logged_in', 'true');
          safeStorage.setItem('travelit_admin_setup', JSON.stringify(data.user));
          
          console.log('✅ TravelIT: Connexion réussie');
          return true;
        } else {
          throw new Error('Réponse invalide du serveur');
        }
      }
    } catch (error) {
      console.error('❌ TravelIT: Erreur lors de la connexion:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    safeStorage.removeItem('travelit_logged_in');
    safeStorage.removeItem('travelit_admin_setup');
    console.log('✅ TravelIT: Déconnexion réussie');
  };

  const hasFeature = (featureName) => {
    if (!user || !user.features) return false;
    return user.features.includes(featureName);
  };

  const isLicenseExpiring = () => {
    // Disabled for demo - always return false
    return false;
    // if (!user || !user.expiryDate) return false;
    // const expiryDate = new Date(user.expiryDate);
    // const now = new Date();
    // const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    // return daysUntilExpiry <= 30;
  };

  const getRemainingDays = () => {
    if (!user || !user.expiryDate) return 0;
    const expiryDate = new Date(user.expiryDate);
    const now = new Date();
    return Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    hasFeature,
    isLicenseExpiring,
    getRemainingDays
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
